import { withErrorBoundary } from 'app/components/error-boundary';
import { images } from 'app/design/images';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import type { NavigationScreen } from 'app/types/navigation';
import { Column } from 'app/design/primitives/box';
import { useNavigationOptions } from 'app/hooks/navigation';
import React from 'react';
import Svg, { Circle, ClipPath, Defs, G, Path, Rect } from 'react-native-svg';
import { Separator } from 'app/design/primitives/text-divider';
import { useMyProfile } from 'app/network/profile';
import { SuperPartageRequirements, useIsSuperRequirements } from 'app/screens/profile/super-partageur';
import { useTranslation } from 'react-i18next';
import { LostSuperProgress } from './lost-super-progress';
import { SuperChangeDescription } from './super-change-description';

function SuperChangeIllustration({ lostSuper }: { lostSuper: boolean }) {
  if (!lostSuper) {
    return <PCImage height={(102 / 204) * 285} source={images.badge.isSuper} width={102} />;
  }

  return (
    <Svg height="92" viewBox="0 0 177 92" width="177">
      <Rect fill="#F1F2F2" height="91.9957" rx="45.9979" width="177" />
      <G clipPath="url(#clip0_6104_4033)">
        <Circle cx="54" cy="45.998" fill="#B8ECE0" r="30" />
        <Path
          d="M42.89 41.21C46.79 35.12 55.61 31.91 66.55 32.55L66.64 32.56C66.85 32.59 67.04 32.69 67.19 32.84L67.25 32.9C67.39 33.07 67.47 33.27 67.48 33.48L67.53 34.51C67.91 44.97 64.71 53.36 58.82 57.14L58.53 57.32C56.66 58.47 54.52 59.11 52.33 59.18L51.89 59.19C49.42 59.19 46.86 58.49 44.33 57.09L41.35 60.07L41.35 60.07C41.29 60.14 41.21 60.2 41.12 60.25L41.03 60.29C40.94 60.33 40.85 60.36 40.75 60.37L40.65 60.38C40.55 60.38 40.45 60.37 40.36 60.34L40.27 60.31C40.17 60.27 40.09 60.22 40.01 60.16L39.94 60.09C39.87 60.02 39.81 59.94 39.76 59.85L39.72 59.76C39.67 59.64 39.65 59.51 39.65 59.38L39.66 59.28C39.67 59.18 39.7 59.09 39.74 59L39.78 58.91C39.81 58.85 39.85 58.8 39.89 58.75L39.96 58.68L42.94 55.7C40.23 50.78 40.13 45.76 42.71 41.5L42.89 41.21ZM65.56 34.47C55.71 34.08 47.94 36.98 44.55 42.27L44.39 42.52C42.28 46 42.29 50.12 44.39 54.25L57.63 41L57.64 41L57.71 40.94C57.89 40.8 58.1 40.73 58.33 40.74C58.58 40.74 58.82 40.84 59 41.02L59.07 41.09C59.21 41.26 59.29 41.48 59.29 41.7L59.29 41.8C59.27 42.02 59.18 42.23 59.03 42.39L59.02 42.39L45.78 55.63C49.91 57.73 54.03 57.74 57.51 55.63L57.66 55.54C63.01 52.19 65.95 44.38 65.56 34.47Z"
          fill="#315B4A"
          stroke="#315B4A"
          strokeWidth="0.2"
        />
      </G>
      <Path
        d="M116.16 45.95C116.16 46.16 116.06 46.37 115.89 46.53L110.85 51.56C110.67 51.73 110.48 51.81 110.27 51.81C109.84 51.81 109.51 51.48 109.51 51.04C109.51 50.84 109.58 50.63 109.73 50.5L111.07 49.1L113.86 46.6L111.95 46.74H102.73C102.28 46.74 101.95 46.41 101.95 45.95C101.95 45.49 102.28 45.16 102.73 45.16H111.95L113.85 45.29L111.07 42.79L109.73 41.4C109.58 41.26 109.51 41.05 109.51 40.85C109.51 40.41 109.84 40.09 110.27 40.09C110.48 40.09 110.67 40.17 110.85 40.33L115.89 45.36C116.06 45.53 116.16 45.73 116.16 45.95Z"
        fill="#1F2635"
      />
      <Path
        d="M140.7 49.7L140.69 48.79C140.63 48.06 140.7 47.39 140.88 46.81C141.05 46.23 141.35 45.7 141.78 45.23C142.22 44.75 142.79 44.31 143.5 43.89C144.18 43.48 144.7 43.11 145.08 42.78C145.46 42.45 145.73 42.1 145.89 41.73C146.05 41.37 146.13 40.94 146.13 40.45V40.44C146.13 39.92 146 39.48 145.75 39.11C145.5 38.72 145.15 38.42 144.7 38.2C144.27 37.98 143.75 37.87 143.16 37.87C142.56 37.87 142.04 37.99 141.59 38.23C141.15 38.47 140.79 38.8 140.53 39.22C140.27 39.63 140.13 40.11 140.09 40.65L140.08 40.78H135.84V40.62C135.88 39.4 136.2 38.32 136.8 37.36C137.4 36.39 138.26 35.63 139.38 35.08C140.49 34.52 141.84 34.25 143.44 34.25C144.93 34.25 146.22 34.5 147.33 35.01C148.44 35.52 149.31 36.23 149.92 37.14C150.55 38.04 150.86 39.1 150.86 40.29V40.31C150.86 41.09 150.73 41.8 150.48 42.44C150.23 43.07 149.86 43.65 149.36 44.17C148.87 44.68 148.25 45.15 147.52 45.59C146.83 45.99 146.29 46.36 145.91 46.7C145.52 47.03 145.25 47.39 145.09 47.76C144.94 48.13 144.86 48.56 144.86 49.06V49.7H140.7ZM142.84 57.19C142.13 57.19 141.53 56.95 141.03 56.48C140.54 56 140.3 55.42 140.3 54.73C140.3 54.04 140.54 53.47 141.03 53C141.53 52.52 142.13 52.28 142.84 52.28C143.56 52.28 144.17 52.52 144.66 53C145.16 53.47 145.41 54.04 145.41 54.73C145.41 55.42 145.16 56 144.66 56.48C144.17 56.95 143.56 57.19 142.84 57.19Z"
        fill="#1F2635"
      />
      <Defs>
        <ClipPath id="clip0_6104_4033">
          <Rect fill="white" height="59.9957" transform="translate(24 16)" width="60" />
        </ClipPath>
      </Defs>
    </Svg>
  );
}

const NotificationSuperChangedScreenComponent: NavigationScreen<'notification-super-changed'> = ({
  navigation,
  route,
}) => {
  const { t } = useTranslation('superPartageurNotification');

  const { data: profile } = useMyProfile();

  useNavigationOptions(navigation, {
    headerBackVisible: false,
  });

  const requirementsList = useIsSuperRequirements();

  const numRequirementsCompleted = requirementsList.filter((req) => req.isCompleted).length;
  const lostSuper = Boolean(route.params.lostSuper);
  const keptSuper = Boolean(route.params.keptSuper);

  const goToDetails = () => {
    navigation.goBack();
    setTimeout(() => {
      navigation.navigate('profile', { screen: 'super-partageur', initial: false });
    }, 300);
  };

  return (
    <Page
      accessory={
        <Accessory marginHorizontal="spacing.general.x5">
          <Button onPress={goToDetails} variant="primary">
            {t('goToDetailsButton')}
          </Button>
        </Accessory>
      }
    >
      <Column alignItems="center" gap="spacing.general.x4">
        <Text fontSize={32} fontWeight="700" textAlign="center">
          {lostSuper ? t('title.lostSuper') : t('title.isSuper', { name: profile?.firstName ?? '' })}
        </Text>

        <SuperChangeIllustration lostSuper={lostSuper} />

        {lostSuper ? <Separator /> : null}

        <SuperChangeDescription keptSuper={keptSuper} lostSuper={lostSuper} />

        {lostSuper ? <Separator /> : null}

        <LostSuperProgress completed={numRequirementsCompleted} lostSuper={lostSuper} />

        <SuperPartageRequirements />
      </Column>
    </Page>
  );
};

export const NotificationSuperChangedScreen: NavigationScreen<'notification-super-changed'> = withErrorBoundary(
  NotificationSuperChangedScreenComponent,
);
